import { inject, Injectable, OnDestroy } from '@angular/core'
import { FeathersService } from '../../feathers.service'
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs'
import { Announcement } from '../../../models/announcement'
import { map, shareReplay, switchMap, tap } from 'rxjs/operators'
import { UsersService } from '../users.service'
import { AnnouncementRead } from '../../../models/announcementRead'

@Injectable({
  providedIn: 'root',
})
export class AnnouncementsService implements OnDestroy {
  private feathers = inject(FeathersService)
  private usersService = inject(UsersService)

  TAG: string = 'AnnouncementsService'
  allStream$: Observable<Announcement[]>
  unreadCountForCurrentUser$$ = new BehaviorSubject<number>(0)
  private currentUserAnnouncementsSub: Subscription
  private unreadCountForCurrentUser$: Observable<number> = this.unreadCountForCurrentUser$$.pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  getAllWithCaching(): Observable<Announcement[]> {
    if (!this.allStream$) this.initAllStream()
    console.log(this.TAG, 'getAllWithCaching', this.allStream$)
    return this.allStream$
  }

  private initAllStream() {
    console.log(this.TAG, 'initAllStream')

    this.allStream$ = this.getAnnouncementsForCurrentUser().pipe(
      tap((a) => console.log('announcements', a)),
      tap((announcements) => {
        this.unreadCountForCurrentUser$$.next(
          announcements.reduce((total, announcement) => (!announcement.isRead ? ++total : total), 0),
        )
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    )

    this.currentUserAnnouncementsSub = this.allStream$.subscribe()
  }

  async send(msg: Announcement) {
    return await this.feathers.createWithFactory(Announcement, msg)
  }

  async patch(id: number, changes: Partial<Announcement>) {
    if (!Object.keys(changes).length) return
    changes.id = id
    return await this.feathers.patchWithFactory(Announcement, changes)
  }

  getUnreadCountForCurrentUser() {
    console.log(this.TAG, 'getUnreadCountForCurrentUser')
    if (!this.allStream$) this.initAllStream()
    return this.unreadCountForCurrentUser$
  }

  async markMessageRead(a: Announcement) {
    if (a.isRead) return
    const currentUser = await this.usersService.getCurrentUserPromise()
    try {
      return this.feathers.createWithFactory(
        AnnouncementRead,
        AnnouncementRead.createFromProps(AnnouncementRead, {
          announcementId: a.id,
          userId: currentUser.id,
        }),
      )
    } catch (error) {
      console.error('Error adding announcement_read record:', error)
      throw error
    }
  }

  async markMessageUnread(a: Announcement) {
    if (!a.isRead) return
    const currentUser = await this.usersService.getCurrentUserPromise()

    if (!a.announcement_reads) return

    const readRecord = a.announcement_reads.find((r) => r.userId === currentUser.id)

    if (!readRecord) {
      console.warn('No announcement_read record found for user', currentUser.id)
      return
    }

    try {
      return await this.feathers.removeById(AnnouncementRead.serviceName, readRecord.id)
    } catch (error) {
      console.error('Error removing announcement_read record:', error)
      if (error.name === 'NotFound') return
      throw error
    }
  }

  ngOnDestroy(): void {
    this.currentUserAnnouncementsSub.unsubscribe()
  }

  delete(a: Announcement) {
    return this.feathers.remove<Announcement>(Announcement.serviceName, a)
  }

  getAnnouncementsForCurrentUser(): Observable<Announcement[]> {
    return this.usersService.getCurrentUser().pipe(
      switchMap((user) => {
        if (user.isAdmin()) {
          // Admin loads all announcements
          const result = this.feathers.find(Announcement)
          return result ? result : of([])
        } else if (!user.isOaClient()) {
          // Client gets no announcements
          const result = this.feathers.find(Announcement, {
            publishedAt: { $ne: null },
          }) as Observable<Announcement[]>
          return result
            ? result.pipe(map((announcements) => announcements.filter((a) => a.publishedAt != null)))
            : of([])
        } else {
          // OA client gets published announcements
          const result = this.feathers.find(Announcement, {
            publishedAt: { $ne: null },
          }) as Observable<Announcement[]>
          return result
            ? result.pipe(map((announcements) => announcements.filter((a) => a.publishedAt != null)))
            : of([])
        }
      }),
    )
  }
}
